import { EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import { usePaginatedCollections } from 'lib-knowledge-management-common/hooks/usePaginatedCollections';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useArticlesFilters } from '../articles/hooks/useArticlesFilters';
import CollectionEmptyPage from '../collectionEmpty';
import { useTestEmptyCollections } from '../collectionEmpty/useTestEmptyCollections';

export function FullPageWrapper({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  return (
    <FlexBox style={{ width: '100%' }} justifyContent="center">
      {children}
    </FlexBox>
  );
}

/**
 * A landing page that checks for existing collections then:
 *
 * 1. Redirects to the 1st collection available.
 * 2. Redirects to the `CreateCollectionPage`, which then forwards to the newly
 *    created collection.
 */
function CollectionRootPage(): JSX.Element {
  const { store: articlesFiltersStore } = useArticlesFilters();
  const { data, error, refetch, loading } = usePaginatedCollections({
    fetchPolicy: 'cache-and-network',
  });
  const testEmptyCollections = useTestEmptyCollections();

  if (!data && loading) {
    return <KnowledgeLoading />;
  }

  if (error || !data) {
    return (
      <EmptyState
        title="Something went wrong"
        description="There was an error fetching collections"
        buttonProps={{
          onClick: () => refetch(),
        }}
      />
    );
  }

  /**
   * Testing the empty collections flow is difficult when you have pre-existing
   * collections.
   *
   * Append the testEmptyCollections=true querystring while testing the
   * `/knowledge/collection` route locally against staging and the app will
   * temporarily return 0 collections, tricking it into the "No collections"
   * flow.
   *
   * To test the functionality out, simply visit
   * http://localhost:8082/knowledge/collection?testEmptyCollections=true
   *
   * You should now see the 'empty collections' page.
   */
  const collectionResults = testEmptyCollections
    ? []
    : data.collections.results;

  if (collectionResults.length === 0 || testEmptyCollections) {
    return (
      <FullPageWrapper>
        <CollectionEmptyPage />
      </FullPageWrapper>
    );
  }

  const firstCollection = collectionResults[0];
  return (
    <Navigate
      to={routeGenerator.collectionEdit({
        collectionId: firstCollection.id,
        articlesFiltersStore,
      })}
      replace={true}
    />
  );
}

export default CollectionRootPage;
